<template>
  <BaseModal class="product-modal" v-model="isOpenModal">
    <template v-slot:header>
      <div class="flex flex-row items-center">{{ header }}</div>
    </template>
    <div class="content relative">
      <button
        class="absolute top-0 right-2 flex flex-row items-center btn btn-dark-button"
        @click="getSyncData"
      >
        <BaseMiniSpinner v-if="loading" class="mr-2" />
        Refresh
      </button>

      <!-- Initial load -->
      <div
        v-if="!hasLoaded"
        class="flex flex-row items-center justify-center gap-2 mt-8 py-8 text-gray-600"
      >
        <BaseMiniSpinner />
        Loading sync data...
      </div>

      <!-- Order doesn't exist in NKRSync -->
      <div v-else-if="!orderData" class="flex flex-col gap-4 mt-8">
        <div
          class="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 rounded-md"
        >
          <div class="flex items-start">
            <i class="mdi mdi-alert text-2xl mr-3"></i>
            <div>
              <p class="font-semibold mb-2">
                Order not found in NKR Sync
              </p>
              <p class="text-sm">
                This order needs to be sent to NKR Sync to enable all
                functionalities (printing, Infoauto export, etc.).
              </p>
              <p class="text-sm mt-2">
                <strong>Note:</strong> New orders usually take a few minutes to
                reach NKR Sync after creation.
              </p>
            </div>
          </div>
        </div>
        <button
          class="btn btn-info-button flex items-center justify-center w-full md:w-auto"
          @click="updateSync"
        >
          <BaseMiniSpinner v-if="sendingNKRSYNC" class="mr-2" />
          Create in NKR Sync
        </button>
      </div>

      <!-- Order exists in NKRSync -->
      <div v-else>
        <!-- Export Status Section -->
        <div v-if="hasExportData" class="mb-6">
          <h3 class="text-lg font-semibold mb-3">Export Status</h3>
          <table class="default-table">
            <thead>
              <tr>
                <th>Export Name</th>
                <th>Status</th>
                <th>Date</th>
                <th>ID Infoauto</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(value, key) in orderData.exportStatus" :key="key">
                <td class="text-center">
                  {{ value.name }}
                </td>
                <td class="text-center">
                  <span
                    :class="{
                      'text-green-600 font-semibold':
                        value.status === 'success',
                      'text-red-600 font-semibold': value.status === 'failed',
                      'text-yellow-600 font-semibold':
                        value.status === 'pending'
                    }"
                  >
                    {{ value.status }}
                  </span>
                </td>
                <td class="text-center">
                  {{ value.date }}
                </td>
                <td class="text-center">
                  {{ value.integratedSystemOrderId || "N/A" }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- No exports yet -->
        <div
          v-else
          class="bg-blue-100 border-l-4 border-blue-500 text-blue-800 p-4 rounded-md mb-6"
        >
          <div class="flex items-start">
            <i class="mdi mdi-information text-2xl mr-3"></i>
            <div>
              <p class="font-semibold mb-1">
                Not sent to Infoauto yet
              </p>
              <p class="text-sm">
                This order hasn't been exported to Infoauto. Use the button
                below to send it.
              </p>
            </div>
          </div>
        </div>

        <!-- Discount Section -->
        <div v-if="orderData.discount > 0" class="mb-6">
          <h3 class="text-lg font-semibold mb-3">Discount</h3>
          <div class="flex items-center gap-4 bg-gray-50 border border-gray-200 rounded-md p-3">
            <div>
              <span class="text-sm text-gray-500">Amount</span>
              <div class="font-semibold">${{ parseFloat(orderData.discount).toFixed(2) }}</div>
            </div>
            <div>
              <span class="text-sm text-gray-500">Type</span>
              <div>
                <span
                  v-if="orderData.discount_type === 'percentage'"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                >
                  % Percentage — {{ orderData.discount_percentage }}%
                </span>
                <span
                  v-else
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700"
                >
                  Fixed Amount
                </span>
              </div>
            </div>
            <div v-if="orderData.discount_coupon">
              <span class="text-sm text-gray-500">Coupon</span>
              <div class="font-mono text-sm">{{ orderData.discount_coupon }}</div>
            </div>
          </div>
        </div>

        <!-- Actions Section -->
        <div class="flex flex-col mb-6">
          <h3 class="text-lg font-semibold mb-3">Actions</h3>

          <!-- Informational Alert for warehouse allocation orders -->
          <div
            v-if="orderStatus === 'warehouse_allocation'"
            class="bg-purple-100 border-l-4 border-purple-500 text-purple-700 p-4 mb-4 rounded-r"
            role="alert"
          >
            <div class="flex items-center">
              <i class="mdi mdi-information-outline text-xl mr-2"></i>
              <div>
                <p class="font-medium">Warehouse Allocation Order</p>
                <p class="text-sm">
                  Sending this order to Infoauto will reserve stock but will not
                  create an invoice. The order will remain in warehouse
                  allocation status.
                </p>
              </div>
            </div>
          </div>

          <div
            class="flex flex-col md:flex-row gap-4 items-stretch md:items-center"
          >
            <!-- Send to Infoauto Button -->
            <button
              class="btn btn-error-button justify-center px-4 py-2"
              v-if="!exported"
              @click="sendOrderToInfoauto"
              :disabled="sendingToInfoauto"
            >
              <BaseMiniSpinner v-if="sendingToInfoauto" class="mr-2" />
              <i class="mdi mdi-send mr-2"></i>
              Send to Infoauto
            </button>

            <!-- Already exported indicator -->
            <div
              v-else
              class="bg-green-100 border border-green-500 text-green-800 px-4 py-2 rounded-md flex items-center"
            >
              <i class="mdi mdi-check-circle text-xl mr-2"></i>
              Already exported to Infoauto
            </div>

            <!-- Print invoice -->
            <a
              :href="
                `https://nkrsync-be.nkrdieselparts.com/orders/print_invoice/${orderData.uuid}`
              "
              target="_blank"
              class="btn btn-dark-button justify-center px-4 py-2 flex items-center gap-2"
            >
              <i class="mdi mdi-printer text-xl"></i>
              Print Pick-Up Order
            </a>

            <!-- Update NKR Sync -->
            <button
              class="btn btn-info-button justify-center px-4 py-2"
              @click="updateSync"
              :disabled="sendingNKRSYNC"
            >
              <BaseMiniSpinner v-if="sendingNKRSYNC" class="mr-2" />
              <i class="mdi mdi-sync mr-2"></i>
              Update NKR Sync
            </button>

            <!-- Show/Hide Logs -->
            <button
              @click="showLogs = !showLogs"
              class="btn btn-secondary-button px-4 py-2"
            >
              <i
                :class="showLogs ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"
                class="mr-2"
              ></i>
              {{ showLogs ? "Hide Logs" : "Show Logs" }}
            </button>
          </div>
        </div>

        <!-- Logs Section -->
        <div v-if="showLogs" class="mt-6">
          <h3 class="text-lg font-semibold mb-3">Logs</h3>
          <p
            v-if="!orderData.logs || orderData.logs.length === 0"
            class="bg-gray-100 p-4 rounded-md text-gray-600"
          >
            No logs available
          </p>
          <table v-else class="default-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(log, index) in orderData.logs" :key="index">
                <td class="text-center">
                  {{ log.date }}
                </td>
                <td class="text-left">
                  {{ log.message }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Send Notification Message Section -->
        <div class="mt-6">
          <h3 class="text-lg font-semibold mb-3">Send Notification Message</h3>
          <div class="flex flex-col gap-3">
            <textarea
              v-model="notificationMessage"
              class="w-full p-3 border border-gray-300 rounded-md resize-y min-h-[100px]"
              placeholder="Enter your message here..."
              :disabled="sendingMessage"
            ></textarea>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                v-model="includeOrderNumber"
                class="w-4 h-4"
                :disabled="sendingMessage"
              />
              <span class="text-sm text-gray-700"
                >Include order number in notification</span
              >
            </label>
            <button
              class="btn btn-primary-button px-4 py-2 self-start flex items-center"
              @click="handleSendNotification"
              :disabled="sendingMessage || !notificationMessage.trim()"
            >
              <BaseMiniSpinner v-if="sendingMessage" class="mr-2" />
              <i v-else class="mdi mdi-send mr-2"></i>
              Send Message
            </button>
          </div>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
<script>
import {
  getOrderInformation,
  updateSyncApi,
  sendOrderToInfoauto as sendOrderToInfoautoApi,
  sendNotificationMessage
} from "@/api/store/NkrsyncOrders.js";

export default {
  components: {},
  props: {
    orderNumber: {
      type: String,
      required: true
    },
    value: {
      type: Boolean,
      required: true
    },
    orderStatus: {
      type: String,
      required: false,
      default: ""
    }
  },
  data() {
    return {
      orderData: null,
      hasLoaded: false,
      isOpenModal: this.value,
      loading: false,
      showLogs: false,
      sendingToInfoauto: false,
      sendingNKRSYNC: false,
      notificationMessage: "",
      sendingMessage: false,
      includeOrderNumber: true
    };
  },
  computed: {
    header() {
      return `Order Sync info for ${this.orderNumber}`;
    },
    isOpen() {
      return this.value;
    },
    exported() {
      return this.orderData?.exportStatus.some(
        item => item.status === "success"
      );
    },
    hasExportData() {
      return (
        this.orderData?.exportStatus && this.orderData.exportStatus.length > 0
      );
    }
  },
  watch: {
    isOpenModal(val) {
      this.$emit("input", val);
    },
    selectedSection() {
      this.showMenu = false;
    },
    value(val) {
      if (val) {
        this.selectedSection = "general";
        this.productFull = null;
        this.orderData = null;
        this.hasLoaded = false;
        this.getSyncData();
        const query = { ...this.$route.query, productOpen: this.code };
        this.$router.replace({ query }).catch(() => {});
      } else {
        const query = { ...this.$route.query };
        delete query.productOpen;
        this.$router.replace({ query }).catch(() => {});
      }
      this.isOpenModal = val;
    }
  },
  methods: {
    updateSync() {
      if (this.sendingNKRSYNC) {
        return;
      }
      this.sendingNKRSYNC = true;
      updateSyncApi(this.orderNumber)
        .then(res => {
          console.log(res);
          let message = "Sync updated";
          switch (res) {
            case "orderUpToDate":
              message = "Order is up to date";
              break;
            case "orderUpdated":
              message = "Order updated";
              break;
            case "orderInserted":
              message = "Order inserted";
              break;
          }
          this.$toast.success(message);
          this.getSyncData();
        })
        .catch(() => {
          this.$toast.error("Error updating sync");
        })
        .finally(() => {
          this.sendingNKRSYNC = false;
        });
    },
    getSyncData() {
      if (this.loading) {
        return;
      }
      this.loading = true;
      getOrderInformation(this.orderNumber)
        .then(response => {
          this.orderData = response;
        })
        .catch(error => {
          console.log(error);
          this.$toast.error("Error getting sync data");
        })
        .finally(() => {
          this.loading = false;
          this.hasLoaded = true;
        });
      console.log("getSyncData");
    },
    sendOrderToInfoauto() {
      if (this.sendingToInfoauto) {
        return;
      }
      this.sendingToInfoauto = true;
      sendOrderToInfoautoApi(this.orderNumber)
        .then(res => {
          console.log(res);
          this.$toast.success("Order sent to Infoauto");
          this.getSyncData();
        })
        .catch(() => {
          this.$toast.error("Error sending order to Infoauto");
        })
        .finally(() => {
          this.sendingToInfoauto = false;
        });
    },
    handleSendNotification() {
      if (this.sendingMessage || !this.notificationMessage.trim()) {
        return;
      }
      this.sendingMessage = true;
      const orderNum = this.includeOrderNumber ? this.orderNumber : null;
      sendNotificationMessage(orderNum, this.notificationMessage)
        .then(() => {
          this.$toast.success("Message sent successfully");
          this.notificationMessage = "";
        })
        .catch(() => {
          this.$toast.error("Error sending message");
        })
        .finally(() => {
          this.sendingMessage = false;
        });
    }
  },
  mounted() {
    if (this.value) {
      this.getSyncData();
    }
  }
};
</script>
<style scoped>
.content {
  font-size: 20px;
}
</style>
